import TutorHomePageV2 from './TutorHomePageV2';
import TutorShoutoutButton from '../Sparks/TutorShoutoutButton';
import { SPARKS_REFRESH_MESSAGE } from '../Sparks/SparksClassSync';
import TutorAssessmentTimer from './TutorAssessmentTimer';
import TutorSingleQuestionPage from './Quiz/TutorSingleQuestionPage/TutorSingleQuestionPage';
import HomePageV2 from '../HomePageV2';
import QuizPage from '../../views/Quiz/Quiz';
import { useEffect, useState, useCallback, useRef } from 'react';
import Tile from '../DailyVideoCalls/Tile/Tile';
import { Ear, EarOff, UserX, Maximize2, Minimize2, Pencil, MessageSquare } from 'lucide-react';
import './TutorLearningSession.css';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import VoiceOverOffIcon from '@mui/icons-material/VoiceOverOff';
import { useHandRaising } from '../DailyVideoCalls/HandRaising/useHandRaising';
import RaiseHandBadge from '../DailyVideoCalls/HandRaising/RaiseHandBadge';
import { useWhiteboard } from '../../whiteboard';
import WhiteboardPanel from '../../whiteboard/WhiteboardPanel';
import LessonOverlayCanvas from '../../whiteboard/LessonOverlayCanvas';
import ResizableDivider from '../../whiteboard/ResizableDivider';
import { ScaledLessonContent } from '../../whiteboard/ScaledLessonContent';
import { MaybeZoom } from '../Quiz/QuestionZoom/QuestionZoom';
import TutorChatDrawer from '../Chat/TutorChatDrawer';

function TutorLearningSession({ 
    studentState, 
    student, 
    canHearTutor, 
    onHearingToggle,
    isMicMuted,
    onMicToggle,
    tutorEmails,
    studentInput,
    timerState,
    onKickStudent,
    studentName,
    callObject,
    localSessionId,
    registerWhiteboardHandler,
    unregisterWhiteboardHandler,
    tutorWhiteboardStoreRef,
    isFocused = false,
    onFocusClick,
    chatMessages,
    chatHistoryCount,
    onChatSend,
    onChatRead,
    chatUnreadCount,
    onChatOpen,
}) {
    const [currentView, setCurrentView] = useState(null);
    const [delayedQuizQuestion, setDelayedQuizQuestion] = useState(null);
    const { handRaisedParticipants } = useHandRaising(tutorEmails);
    const userHasHandRaised = handRaisedParticipants?.includes(student.session_id);

    const whiteboard = useWhiteboard({
        studentId: student.session_id,
        targetParticipantId: student.session_id,
        localParticipantId: localSessionId,
        callObject,
        isTutor: true,
        tutorWhiteboardStoreRef: tutorWhiteboardStoreRef ?? null,
        persistedStudentId: student.session_id,
        isFocused,
    });

    useEffect(() => {
        if (registerWhiteboardHandler && unregisterWhiteboardHandler) {
            registerWhiteboardHandler(student.session_id, whiteboard.handleReceive);
            return () => unregisterWhiteboardHandler(student.session_id);
        }
    }, [student.session_id, registerWhiteboardHandler, unregisterWhiteboardHandler, whiteboard.handleReceive]);
    
   /* useEffect(() => {
        console.log('Student State in TutorLearningSession:', studentState);
    }, [studentState]);*/

    useEffect(() => {
        setCurrentView(studentState?.currentView);
    }, [studentState?.currentView]);

    // Step change: clear lesson overlay when lesson/quiz step changes
    const prevStepKeyRef = useRef(null);
    useEffect(() => {
        if (!callObject || !registerWhiteboardHandler) return;
        const stepKey = `${studentState?.currentView ?? ''}-${studentState?.quizSubmission?.id ?? ''}-${delayedQuizQuestion ?? ''}`;
        if (prevStepKeyRef.current !== null && prevStepKeyRef.current !== stepKey) {
            whiteboard.clearSurface('lesson');
        }
        prevStepKeyRef.current = stepKey;
    }, [
        studentState?.currentView,
        studentState?.quizSubmission?.id,
        delayedQuizQuestion,
        callObject,
        registerWhiteboardHandler,
        whiteboard.clearSurface,
    ]);

    // Derive a stable key that only changes when the student is genuinely on a
    // different question. Using currentQuestionId (set in Quiz.js's onStateChange
    // and broadcast via learning-state-update) rather than filtering questionAttempts
    // for 'not attempted' status fixes a premature-advance bug: when a student
    // submits Q1 correctly its status flips to 'correct', the old filter's [0]
    // would immediately return Q2, and the tutor's tile would advance before the
    // student clicked Next. currentQuestionId only changes when the student
    // actually navigates to the next question.
    const questionKey = studentState?.showingExplanation
        ? studentState?.currentExplanationQuestion?._id
        : studentState?.currentQuestionId ?? null;

    useEffect(() => {
        if (currentView !== 'quiz') return;

        if (studentState?.showingExplanation && studentState.currentExplanationQuestion) {
            const timer = setTimeout(() => {
                setDelayedQuizQuestion(studentState.currentExplanationQuestion);
            }, 500);
            return () => clearTimeout(timer);
        }

        if (questionKey && studentState?.quizSubmission) {
            // Look up the question object by ID rather than by filter position so
            // that a status change (not attempted → correct) never advances the key.
            const currentQuestion =
                studentState.quizSubmission.questionAttempts
                    ?.find(a => a.LessonQuestionId?._id === questionKey)
                    ?.LessonQuestionId ?? null;

            const timer = setTimeout(() => {
                setDelayedQuizQuestion(currentQuestion);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [questionKey, currentView, studentState?.showingExplanation, studentState?.currentExplanationQuestion]);

    // Close whiteboard when unfocusing (e.g. exit focus) so student gets sync
    useEffect(() => {
        if (!isFocused) {
            whiteboard.setWhiteboardOpen(false);
        }
    }, [isFocused, whiteboard.setWhiteboardOpen]);

    useEffect(() => {
        return () => {
            whiteboard.setWhiteboardOpen(false);
        };
    }, [whiteboard.setWhiteboardOpen]);

    const showWhiteboard = isFocused && callObject && registerWhiteboardHandler && whiteboard.whiteboardOpen;

    return (
        <div className={`tutor-session-tile ${isFocused ? 'tutor-session-tile--with-whiteboard tutor-session-tile--focused' : ''}`}>
            <div className="tutor-session-tile__body">
            {/* Add hand raise badge at the top */}
            {userHasHandRaised && (

                    <RaiseHandBadge id={student.session_id} isLocal={false} />

            )}

            {/* Sparks: the child's balance travels in learning-state-update; the shout-out
                button awards +100 (max 2/child/day, reason audited) and pings the child's
                call so it shows at once — Sparks §2.3, §2.6. */}
            <div className="tutor-session-sparks" style={{ position: 'absolute', top: 6, left: 6, zIndex: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
                {typeof studentState?.sparksBalance === 'number' && (
                    <span className="px-2 py-0.5 rounded-full bg-[#FFD966] text-[#2F1E6B] text-xs font-bold" title="Student's Sparks">
                        {studentState.sparksBalance.toLocaleString('en-GB')} ✨
                    </span>
                )}
                <TutorShoutoutButton
                    childId={student?.userData?.mongoChildId}
                    childName={studentName}
                    onSent={() => callObject?.sendAppMessage({ type: SPARKS_REFRESH_MESSAGE }, student.session_id)}
                />
            </div>

            {/* Countdown pill for timed assessments (top-right corner is the
                only one not already occupied by controls or the video tile) */}
            {currentView === 'quiz'
                && studentState?.quizSubmission?.timerEnabled
                && (studentState?.quizStatus ?? studentState?.quizSubmission?.QuizStatus) !== 'Complete'
                && timerState != null && (
                <TutorAssessmentTimer
                    remainingSeconds={timerState.remainingSeconds}
                    syncedAt={timerState.syncedAt}
                />
            )}

            <div className="tutor-session-content">
                {/* Left: lesson content + overlay */}
                <div
                    className={`tutor-session-lesson-area${showWhiteboard ? ' tutor-session-lesson-area--whiteboard-active' : ''}`}
                    style={{
                        flex: showWhiteboard
                            ? 1 - (whiteboard.whiteboardRatio ?? 0.3)
                            : 1,
                    }}
                >
                    <ScaledLessonContent
                        active={showWhiteboard}
                        containerRef={whiteboard.lessonContainerRef}
                        outerContainerRef={whiteboard.lessonOuterContainerRef}
                    >
                        {showWhiteboard ? (
                            <>
                                {currentView === 'lesson' && studentState?.latestLesson != null && (
                                    <HomePageV2
                                        isTutorPreview={true}
                                        previewState={{
                                            latestLesson: studentState.latestLesson,
                                            currentStep: studentState.currentStep,
                                        }}
                                        preventNavigation={true}
                                    />
                                )}
                                {currentView === 'quiz' && studentState?.quizSubmission && (
                                    <QuizPage
                                        isTutorPreview={true}
                                        previewState={{
                                            quizSubmission: studentState.quizSubmission,
                                            currentQuestionIndex: studentState.currentQuestionIndex,
                                            currentQuestionId: studentState.currentQuestionId,
                                            showingExplanation: studentState.showingExplanation,
                                            currentExplanationQuestion: studentState.currentExplanationQuestion,
                                        }}
                                        quizSubmissionId={studentState.quizSubmission._id}
                                        lessonId={studentState.quizSubmission.lessonId?._id ?? studentState.quizSubmission.lessonId}
                                        topicId={studentState.quizSubmission.topicId?._id ?? studentState.quizSubmission.topicId}
                                        preventNavigation={true}
                                    />
                                )}
                                <LessonOverlayCanvas
                                    lessonCanvasRef={whiteboard.lessonCanvasRef}
                                    lessonContainerRef={whiteboard.lessonContainerRef}
                                    getPointerHandlers={whiteboard.getPointerHandlers}
                                    isActive={whiteboard.isActive}
                                    tool={whiteboard.tool}
                                    remoteCursor={whiteboard.remoteCursor}
                                />
                            </>
                        ) : (
                            /* In focus mode (no whiteboard), give the tutor the same zoom
                               control as the student so they can enlarge the student's
                               work. fillHeight only passes the height through (the image
                               size overrides are scoped to classroom, not here). */
                            <MaybeZoom enabled={isFocused} fillHeight>
                                {currentView === 'lesson' && (
                                    <TutorHomePageV2
                                        studentState={studentState}
                                        studentInput={studentInput}
                                        isFocused={isFocused}
                                    />
                                )}
                                {currentView === 'quiz' && studentState?.quizSubmission && (
                                    <TutorSingleQuestionPage
                                        quizQuestion={delayedQuizQuestion}
                                        ButtonsAreClickable={false}
                                        onAnswerSelect={() => {}}
                                        studentInput={studentInput}
                                        isFocused={isFocused}
                                    />
                                )}
                            </MaybeZoom>
                        )}
                    </ScaledLessonContent>
                </div>
                {showWhiteboard && (
                    <ResizableDivider
                        whiteboardRatio={whiteboard.whiteboardRatio ?? 0.3}
                        onDrag={whiteboard.setWhiteboardRatio}
                        onDragEnd={whiteboard.onLayoutDragEnd}
                        canResize={true}
                    />
                )}
                {showWhiteboard && (
                    <div
                        className="tutor-session-whiteboard-area"
                        style={{ flex: whiteboard.whiteboardRatio ?? 0.3 }}
                    >
                        <WhiteboardPanel
                            whiteboardCanvasRef={whiteboard.whiteboardCanvasRef}
                            whiteboardContainerRef={whiteboard.whiteboardContainerRef}
                            getPointerHandlers={whiteboard.getPointerHandlers}
                            tool={whiteboard.tool}
                            setTool={whiteboard.setTool}
                            penColor={whiteboard.penColor}
                            setPenColor={whiteboard.setPenColor}
                            isActive={whiteboard.isActive}
                            setIsActive={whiteboard.setIsActive}
                            clearSurface={whiteboard.clearSurface}
                            clearAll={whiteboard.clearAll}
                            isTutor={true}
                            remoteCursor={whiteboard.remoteCursor}
                        />
                    </div>
                )}
            </div>

            {/* Tile component rendered once, outside of conditional blocks */}
            <div key={student.session_id} className="absolute bottom-0 right-0">
                <Tile 
                    key={student.session_id} 
                    id={student.session_id} 
                    StudentWatchMode
                    lessonView = { true }
                />
            </div>

            {/* Modified ear icon div */}
            <div 
                className="absolute bottom-0 left-0 p-2 cursor-pointer z-50" 
                onClick={(e) => { e.stopPropagation(); onHearingToggle(); }}
            >
                {canHearTutor ? <Ear /> : <EarOff color="#f63135"/>}
            </div>

            {/* Add mic control next to hearing control */}
            <div 
                className="absolute bottom-0 left-12 p-2 cursor-pointer z-50" 
                onClick={(e) => { e.stopPropagation(); onMicToggle(); }}
            >
                {isMicMuted ? 
                    <VoiceOverOffIcon sx={{ color: '#f63135' }} /> : 
                    <RecordVoiceOverIcon  />
                }
            </div>

            {/* Add kick student button next to mic control */}
            {onKickStudent && (
                <div 
                    className="absolute bottom-0 left-24 p-2 cursor-pointer z-50" 
                    onClick={(e) => { e.stopPropagation(); onKickStudent(); }}
                >
                    <UserX color="#f63135" />
                </div>
            )}

            {/* Whiteboard toggle: open/close whiteboard (tutor only, when focused) */}
            {isFocused && callObject && registerWhiteboardHandler && (
                <div
                    className={`tutor-whiteboard-btn ${whiteboard.whiteboardOpen ? 'active' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        whiteboard.setWhiteboardOpen(!whiteboard.whiteboardOpen);
                    }}
                    title="Open/Close whiteboard"
                >
                    <Pencil size={20} />
                </div>
            )}

            {/* Chat button: always visible next to focus button */}
            {onChatOpen && (
                <div
                    className="tutor-chat-btn"
                    onClick={(e) => { e.stopPropagation(); onChatOpen(); }}
                    title="Open chat"
                >
                    <MessageSquare size={20} />
                    {(chatUnreadCount ?? 0) > 0 && (
                        <span className="tutor-chat-btn__badge">
                            {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                        </span>
                    )}
                </div>
            )}

            {/* Focus button: focus on this student or exit focus (only when 2+ students) */}
            {onFocusClick && (
                <div
                    className="tutor-focus-btn"
                    onClick={(e) => { e.stopPropagation(); onFocusClick(); }}
                    title={isFocused ? 'Exit focus (Escape)' : 'Focus on student'}
                >
                    {isFocused ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </div>
            )}
            </div>

            {isFocused && onChatSend && (
                <TutorChatDrawer
                    studentName={studentName}
                    messages={chatMessages ?? []}
                    onSend={onChatSend}
                    onRead={onChatRead}
                    historyCount={chatHistoryCount ?? 0}
                />
            )}
        </div>
    );
}

export default TutorLearningSession;